import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import Button from "../Button";
import Select from "../inputs/Select";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

interface PaginationProps {
    pageIndex: number;
    pageSize: number;
    totalItems: number;
    onPageChange: (pageIndex: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    pageSizeOptions?: number[];
    showSizeChanger?: boolean;
}

function Pagination({
    pageIndex,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = PAGE_SIZE_OPTIONS,
    showSizeChanger = true,
}: PaginationProps) {
    const { t } = useTranslation();

    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    const currentPage = pageIndex + 1;

    const firstItem = totalItems > 0 ? pageIndex * pageSize + 1 : 0;
    const lastItem = Math.min((pageIndex + 1) * pageSize, totalItems);

    return (
        <div className="tw-flex tw-items-center tw-justify-between tw-py-2">
            <div className="tw-flex-1 tw-text-muted-foreground tw-text-sm tw-gap-1 tw-flex">
                <span>{t("displaying-records", { first: firstItem, last: lastItem, total: totalItems })}</span>
            </div>

            <div className="tw-flex tw-items-center tw-space-x-6 lg:tw-space-x-8">
                {showSizeChanger && (
                    <div className="tw-flex tw-items-center tw-space-x-2">
                        <p className="tw-font-medium tw-text-sm">{t("rows-per-page")}</p>
                        <Select
                            className="tw-w-[70px]"
                            showSearch={false}
                            value={`${pageSize}`}
                            onChange={(value?: string) => onPageSizeChange(Number(value))}
                        >
                            {pageSizeOptions.map((option) => (
                                <Select.Item key={option} value={`${option}`}>
                                    {option}
                                </Select.Item>
                            ))}
                        </Select>
                    </div>
                )}
                <div className="tw-flex tw-w-[100px] tw-items-center tw-justify-center tw-font-medium tw-text-sm">
                    {t("page", {
                        current: currentPage,
                        total: totalPages,
                    })}
                </div>
                <div className="tw-flex tw-items-center tw-space-x-2">
                    <Button
                        variant="outline"
                        className="tw-hidden tw-h-8 tw-w-8 tw-p-0 lg:tw-flex"
                        onClick={() => onPageChange(0)}
                        disabled={currentPage === 1}
                    >
                        <span className="tw-sr-only">{t("go-to-first-page")}</span>
                        <ChevronsLeft className="tw-h-4 tw-w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="tw-h-8 tw-w-8 tw-p-0"
                        onClick={() => onPageChange(pageIndex - 1)}
                        disabled={currentPage === 1}
                    >
                        <span className="tw-sr-only">{t("go-to-previous-page")}</span>
                        <ChevronLeft className="tw-h-4 tw-w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="tw-h-8 tw-w-8 tw-p-0"
                        onClick={() => onPageChange(pageIndex + 1)}
                        disabled={currentPage === totalPages}
                    >
                        <span className="tw-sr-only">{t("go-to-next-page")}</span>
                        <ChevronRight className="tw-h-4 tw-w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="tw-hidden tw-h-8 tw-w-8 tw-p-0 lg:tw-flex"
                        onClick={() => onPageChange(totalPages - 1)}
                        disabled={currentPage === totalPages}
                    >
                        <span className="tw-sr-only">{t("go-to-last-page")}</span>
                        <ChevronsRight className="tw-h-4 tw-w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default Pagination;
